import { createDebugger } from '~/scripts/utils/debug.js';
import { fetchMenuInfo, renderMenuInfo } from '~/scripts/utils/menu-info.js';

const debug = createDebugger('mega-menu', false);

const SELECTORS = {
    trigger:      '[data-mega-menu-trigger]',
    dropdown:     '[data-mega-menu-dropdown]',
    child:        '[data-mega-menu-child]',
    grandchildren:'[data-mega-menu-grandchildren]',
    item:         '[data-mega-menu-item]',
    infoPanel:    '[data-menu-info-panel]'
};

const CSS = {
    hidden: 'js-hidden'
};

const HOVER_DEBOUNCE_MS  = 80;
const PREFETCH_STAGGER_MS = 60;
const FADE_MS             = 150;

/**
 * BrittfurnMegaMenu
 *
 * Two layout modes, set via data-layout attribute:
 *
 * sidebar (default): Left sidebar category switching (hover) with content panels.
 *   Info panel on the right populated from the hovered sidebar item's metafield.
 *
 * grid: All groups displayed at once in a wrapping flex layout. No sidebar.
 *   Info panel on the right updates when hovering individual items with data-menu-info-url.
 */
class BrittfurnMegaMenu extends HTMLElement {
    connectedCallback() {
        this.trigger   = this.querySelector(SELECTORS.trigger);
        this.dropdown  = this.querySelector(SELECTORS.dropdown);
        this.infoPanel = this.querySelector(SELECTORS.infoPanel);
        this.layout    = this.dataset.layout || 'sidebar';

        if (!this.dropdown) {
            debug.warn('aborting, missing dropdown');
            return;
        }

        this._fetched           = new Set();
        this._infoTemplate      = this.querySelector('[data-menu-info-template]');
        this._shortcutTemplate  = this.querySelector('[data-menu-info-shortcut-template]');
        this._hoverTimer        = null;
        this._currentInfoHandle = null; // tracks which handle should currently be visible

        if (this.infoPanel) {
            this.infoPanel.style.position = 'relative';
        }

        this.addEventListener('mouseenter', () => {
            debug.log('mouseenter [component]', { id: this.id, layout: this.layout });
            if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
        });
        this.addEventListener('mouseleave', () => {
            debug.log('mouseleave [component]', { id: this.id });
            if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
            clearTimeout(this._hoverTimer);
        });

        if (this.layout === 'grid') {
            this._setupGridListeners();
        } else {
            this._setupSidebarListeners();
        }

        this._setupHoverImages();
    }

    // ----------------------------------------------------------------
    // Hover images
    //
    // Hovering a grandchild link swaps the info panel image for that link's
    // own image, and restores the original on the way out. The markup side
    // lives in snippets/nav-mega-menu.liquid (data-hover-image).
    //
    // NOTE: this only ever existed in the compiled assets/main.bundle.js.
    // It was edited straight into the bundle, so it was invisible to the repo
    // and any clean `vite build` silently deleted the feature. Restored to
    // source 2026-08-04. Do not patch the bundle by hand, edit this file.
    // ----------------------------------------------------------------

    _setupHoverImages() {
        const links = [...this.querySelectorAll('[data-hover-image]')];
        if (links.length === 0) return;

        debug.log('hover images', { count: links.length });
        this._hoverSwappedImg = null;

        links.forEach((el) => {
            el.addEventListener('mouseenter', () => {
                this._setHoverImage(el.dataset.hoverImage, el.dataset.hoverImageAlt || '');
            });
        });

        [...this.querySelectorAll(SELECTORS.grandchildren)].forEach((panel) => {
            panel.addEventListener('mouseleave', () => this._restoreHoverImage());
        });
        this.dropdown.addEventListener('mouseleave', () => this._restoreHoverImage());

        // Warm the cache once per component, staggered so it never competes with
        // the images the customer is actually looking at.
        this.addEventListener('mouseenter', () => {
            if (this._hoverImagesPrefetched) return;
            this._hoverImagesPrefetched = true;
            links.forEach((el, i) => {
                setTimeout(() => {
                    new Image().src = el.dataset.hoverImage;
                }, i * PREFETCH_STAGGER_MS);
            });
        });
    }

    _setHoverImage(src, alt) {
        if (!src || !this.infoPanel) return;

        const panels = [...this.infoPanel.querySelectorAll('[data-menu-info-item]')];
        const panel =
            panels.find((p) => !p.hidden && p.dataset.menuInfoItem === this._currentInfoHandle) ||
            panels.find((p) => !p.hidden);
        const img = panel?.querySelector('[data-field="image"], img');
        if (!img) return;

        // Swapping straight from one hovered link to another must not lose the
        // original, so restore the previous image before taking over a new one.
        if (this._hoverSwappedImg && this._hoverSwappedImg !== img) this._restoreHoverImage();

        if (!('origSrc' in img.dataset)) {
            img.dataset.origSrc    = img.getAttribute('src') || '';
            img.dataset.origAlt    = img.getAttribute('alt') || '';
            img.dataset.origHidden = img.classList.contains(CSS.hidden) ? '1' : '';
        }

        this._hoverSwappedImg = img;
        img.classList.remove(CSS.hidden);
        img.src = src;
        img.alt = alt;
    }

    _restoreHoverImage() {
        const img = this._hoverSwappedImg;
        if (!img) return;

        this._hoverSwappedImg = null;
        img.src = img.dataset.origSrc || '';
        img.alt = img.dataset.origAlt || '';
        if (img.dataset.origHidden) img.classList.add(CSS.hidden);

        delete img.dataset.origSrc;
        delete img.dataset.origAlt;
        delete img.dataset.origHidden;
    }

    // ----------------------------------------------------------------
    // Sidebar layout
    // ----------------------------------------------------------------

    _setupSidebarListeners() {
        this.childLinks       = [...this.querySelectorAll(SELECTORS.child)];
        this.grandchildPanels = [...this.querySelectorAll(SELECTORS.grandchildren)];
        this._activeChild     = null;
        this._activePanel     = null;

        debug.log('connectedCallback [sidebar]', {
            id: this.id,
            childLinks: this.childLinks.length,
            grandchildPanels: this.grandchildPanels.length,
            linksWithMenuInfo: this.childLinks.filter(l => l.dataset.menuInfoUrl).map(l => l.dataset.megaMenuChild)
        });

        if (this.childLinks.length === 0) {
            debug.warn('aborting, no child links');
            return;
        }

        this.childLinks.forEach((link) => {
            const handle = link.dataset.megaMenuChild;
            const panel  = this.querySelector(`[data-mega-menu-grandchildren="${handle}"]`);

            debug.log('binding child', { handle, hasPanel: !!panel, hasMenuInfoUrl: !!link.dataset.menuInfoUrl });

            link.addEventListener('mouseenter', () => {
                debug.log('mouseenter [child]', { handle, hasPanel: !!panel });
                clearTimeout(this._hoverTimer);
                this._hoverTimer = setTimeout(() => {
                    this._currentInfoHandle = link.dataset.megaMenuChild;
                    this._activateChild(link, panel);
                    this._loadMenuInfo(link.dataset.megaMenuChild, link.dataset.menuInfoUrl);
                }, HOVER_DEBOUNCE_MS);
            });
        });

        this.dropdown.addEventListener('mouseleave', () => {
            clearTimeout(this._hoverTimer);
            debug.log('mouseleave [dropdown]');
            this._currentInfoHandle = null;
            this._activateFirstChild();
            this._showInfoPanel(null);
        });

        // On menu open: activate first child + kick off prefetches for all items
        this.addEventListener('mouseenter', () => {
            debug.log('mouseenter [component/sidebar], prefetching all');
            const firstHandle = this.childLinks[0].dataset.megaMenuChild;
            this._currentInfoHandle = firstHandle;
            this._loadMenuInfo(firstHandle, this.childLinks[0].dataset.menuInfoUrl);
            this._prefetchAll();
        });

        this._setupGroupFlyouts();
        this._activateFirstChild();
    }

    // ----------------------------------------------------------------
    // Group flyouts (Sam 2026-08-12)
    //
    // Panels with grouped grandchildren (Mobler: Bord/Stolar & pallar/...)
    // render as two columns: a group list and one stacked sub-panel per
    // group. Clicking a group row reveals its subcategories.
    // ----------------------------------------------------------------

    _setupGroupFlyouts() {
        this.querySelectorAll('[data-mega-groups]').forEach((container) => {
            const rows   = [...container.querySelectorAll('[data-mega-group]')];
            const panels = [...container.querySelectorAll('[data-mega-group-panel]')];
            if (!rows.length) return;

            debug.log('_setupGroupFlyouts', { rows: rows.length, panels: panels.length });

            rows.forEach((row) => {
                row.addEventListener('click', (e) => {
                    e.preventDefault();
                    const handle = row.dataset.megaGroup;
                    const panel  = container.querySelector(`[data-mega-group-panel="${handle}"]`);
                    if (!panel) { debug.warn('group flyout: no panel', { handle }); return; }
                    if (row.classList.contains('is-active')) return;

                    rows.forEach((r) => {
                        const active = r === row;
                        r.classList.toggle('is-active', active);
                        r.setAttribute('aria-selected', active ? 'true' : 'false');
                    });
                    panels.forEach((p) => {
                        if (p === panel) {
                            this._fadeInElement(p, CSS.hidden);
                        } else if (!p.classList.contains(CSS.hidden)) {
                            this._fadeOutElement(p, CSS.hidden);
                        }
                    });
                });
            });
        });
    }

    _prefetchAll() {
        this.childLinks.forEach((link, i) => {
            const handle = link.dataset.megaMenuChild;
            const url    = link.dataset.menuInfoUrl;
            if (!url || this._fetched.has(handle)) return;
            setTimeout(() => {
                this._loadMenuInfo(handle, url);
            }, i * PREFETCH_STAGGER_MS);
        });
    }

    _activateFirstChild() {
        const first  = this.childLinks[0];
        if (!first) return;
        const handle = first.dataset.megaMenuChild;
        const panel  = this.querySelector(`[data-mega-menu-grandchildren="${handle}"]`);
        this._activateChild(first, panel);
    }

    _activateChild(newChild, newPanel) {
        const handle = newChild?.dataset?.megaMenuChild ?? '?';
        if (!newChild || !newPanel) {
            debug.warn('_activateChild: missing child or panel', { handle, hasPanel: !!newPanel });
            return;
        }
        if (this._activeChild === newChild) {
            debug.log('_activateChild: already active, skipping', { handle });
            return;
        }
        debug.log('_activateChild', { handle });

        const prevPanel = this._activePanel;
        const prevChild = this._activeChild;

        if (prevChild) {
            prevChild.setAttribute('aria-selected', 'false');
            prevChild.setAttribute('tabindex', '-1');
        }

        newChild.setAttribute('aria-selected', 'true');
        newChild.setAttribute('tabindex', '0');
        this._activeChild = newChild;
        this._activePanel = newPanel;

        // Fade the new panel in; fade the old one out after
        this._fadeInElement(newPanel, CSS.hidden);
        if (prevPanel && prevPanel !== newPanel) {
            this._fadeOutElement(prevPanel, CSS.hidden);
        }

        const ctaLink = this.querySelector('[data-mega-menu-cta-link]');
        if (ctaLink) {
            const showAllText = newChild.dataset.showAllText;
            if (showAllText) {
                ctaLink.textContent = showAllText;
            } else {
                const title = newChild.querySelector('.mega-menu-child-label')?.textContent?.trim() ?? '';
                const prefix = ctaLink.dataset.viewAllPrefix || '';
                ctaLink.textContent = prefix ? `${prefix} ${title}` : title;
            }
            ctaLink.href = newChild.getAttribute('href') || ctaLink.href;
        }
    }

    // ----------------------------------------------------------------
    // Grid layout
    // ----------------------------------------------------------------

    _setupGridListeners() {
        const items = [...this.querySelectorAll(SELECTORS.item)];

        debug.log('connectedCallback [grid]', {
            id: this.id,
            items: items.length,
            itemsWithMenuInfo: items.filter(i => i.dataset.menuInfoUrl).map(i => i.dataset.megaMenuItem)
        });

        // Make pre-existing info items absolutely positioned for stacking
        if (this.infoPanel) {
            [...this.infoPanel.querySelectorAll('[data-menu-info-item]')].forEach((el) => {
                el.style.position = 'absolute';
                el.style.inset    = '0';
            });
        }

        items.forEach((item) => {
            item.addEventListener('mouseenter', () => {
                debug.log('mouseenter [grid item]', { handle: item.dataset.megaMenuItem, hasUrl: !!item.dataset.menuInfoUrl });
                clearTimeout(this._hoverTimer);
                this._hoverTimer = setTimeout(() => {
                    this._currentInfoHandle = item.dataset.megaMenuItem;
                    this._loadMenuInfo(item.dataset.megaMenuItem, item.dataset.menuInfoUrl);
                }, HOVER_DEBOUNCE_MS);
            });
        });

        // Prefetch all items on menu open
        this.addEventListener('mouseenter', () => {
            this._preloadPanelImages();
            items.forEach((item, i) => {
                const handle = item.dataset.megaMenuItem;
                const url    = item.dataset.menuInfoUrl;
                if (!url || !handle || this._fetched.has(handle)) return;
                setTimeout(() => {
                    this._loadMenuInfo(handle, url);
                }, i * PREFETCH_STAGGER_MS);
            });
        });

        this.dropdown.addEventListener('mouseleave', () => {
            clearTimeout(this._hoverTimer);
            debug.log('mouseleave [dropdown/grid]');
            this._currentInfoHandle = 'default';
            this._showInfoPanel('default');
        });
        this._currentInfoHandle = 'default';
        this._showInfoPanel('default');
    }

    // ----------------------------------------------------------------
    // Info panel (shared)
    // ----------------------------------------------------------------

    /**
     * Bilder i förrenderade infopaneler (Stilguidens kategorirutor) ligger lazy och
     * i en dold panel, utan detta börjar de ladda först när kunden hovrar stilen,
     * och rutorna står tomma en stund. Görs en gång, vid första menyöppningen.
     */
    _preloadPanelImages() {
        if (this._panelImagesPreloaded || !this.infoPanel) return;
        this._panelImagesPreloaded = true;
        this.infoPanel.querySelectorAll('img[loading="lazy"]').forEach((img) => {
            img.loading = 'eager';
            img.src = img.src; // eslint-disable-line no-self-assign, startar hämtningen
        });
    }

    async _loadMenuInfo(handle, fetchUrl) {
        if (!this.infoPanel || !this._infoTemplate) {
            debug.warn('_loadMenuInfo: missing infoPanel or template', { handle });
            return;
        }

        // En panel som redan är renderad i Liquid (Stilguidens kategorirutor) vinner
        // ALLTID över menu_info-hämtningen. Stilarna bär både och, utan den här
        // kontrollen först tar hämtvägen över och rutorna syns aldrig.
        // OBS: ingen CSS.escape här, bundlern döper om den globala CSS-symbolen
        // (den krockar med en annan modul) och anropet kastar tyst i en async-metod.
        const preRendered = handle
            ? [...this.infoPanel.children].find((el) => el.dataset.menuInfoItem === handle)
            : null;
        if (preRendered) {
            debug.log('_loadMenuInfo: förrenderad panel', { handle });
            // Samma vakt som hämtvägen: förhämtningen vid menyöppning gar igenom alla
            // poster, och utan den har skulle sista posten visa sin panel i stallet for
            // den kunden faktiskt hovrar.
            if (this._currentInfoHandle === handle) this._showInfoPanel(handle);
            return;
        }

        if (!fetchUrl) {
            debug.log('_loadMenuInfo: no url', { handle });
            this._showInfoPanel('default');
            return;
        }

        if (this._fetched.has(handle)) {
            debug.log('_loadMenuInfo: cached', { handle });
            this._showInfoPanel(handle);
            return;
        }
        this._fetched.add(handle);

        debug.log('_loadMenuInfo: fetching', { handle, fetchUrl });

        try {
            const result = await fetchMenuInfo(fetchUrl);

            if (!result) {
                debug.warn('_loadMenuInfo: no data, metafield blank, showing default', { handle });
                this._fetched.delete(handle);
                if (this._currentInfoHandle === handle) this._showInfoPanel('default');
                return;
            }

            const panel = document.createElement('div');
            panel.dataset.menuInfoItem = handle;
            panel.className = 'h-full overflow-hidden';
            panel.style.cssText = 'position:absolute;inset:0;opacity:0;';
            panel.hidden = true;

            renderMenuInfo(panel, this._infoTemplate, this._shortcutTemplate, result);

            // Inject pre-rendered vendor list (from Liquid template) into the shortcuts column
            const vendorTpl      = this.querySelector(`[data-vendor-list="${handle}"]`);
            const vendorsSection = panel.querySelector('[data-vendors-section]');
            const vendorsList    = panel.querySelector('[data-vendors-list]');
            if (vendorTpl && vendorsSection && vendorsList) {
                vendorsList.innerHTML = vendorTpl.innerHTML;
                vendorsSection.classList.remove('hidden');
                // Show the outer shortcuts column even if there are no shortcuts (vendors alone justify it)
                panel.querySelector('[data-shortcuts-col]')?.classList.remove('js-hidden');
            }

            this.infoPanel.appendChild(panel);

            // Only reveal if this handle is still the one the user is on
            if (this._currentInfoHandle === handle) {
                this._showInfoPanel(handle);
            }
        } catch (err) {
            debug.error('_loadMenuInfo: failed', { handle, fetchUrl, err });
            this._fetched.delete(handle);
            if (this._currentInfoHandle === handle) this._showInfoPanel('default');
        }
    }

    _showInfoPanel(handle) {
        const panels = this.infoPanel ? [...this.infoPanel.querySelectorAll('[data-menu-info-item]')] : [];
        debug.log('_showInfoPanel', { handle, panelCount: panels.length, panelHandles: panels.map(p => p.dataset.menuInfoItem) });

        panels.forEach((el) => {
            const isTarget = el.dataset.menuInfoItem === handle;
            if (isTarget) {
                // Show: fade in if currently hidden, or ensure opacity is 1 if already visible
                if (el.hidden || parseFloat(el.style.opacity || '1') < 0.5) {
                    this._fadeInElement(el);
                }
            } else {
                // Hide: fade out only if currently visible
                if (!el.hidden) {
                    this._fadeOutElement(el);
                }
            }
        });
    }

    // ----------------------------------------------------------------
    // Animation helpers
    // ----------------------------------------------------------------

    /**
     * Fade an element in by removing hiddenClass, then transitioning opacity 0→1.
     */
    _fadeInElement(el, hiddenClass) {
        // Cancel any in-progress fade-out timer on this element
        if (el._fadeTimer) {
            clearTimeout(el._fadeTimer);
            el._fadeTimer = null;
        }

        if (hiddenClass) el.classList.remove(hiddenClass);
        el.hidden = false;
        el.style.transition = 'none';
        el.style.opacity    = '0';
        // Force reflow so the initial opacity:0 is painted before we start the transition
        el.offsetHeight; // eslint-disable-line no-unused-expressions
        el.style.transition = `opacity ${FADE_MS}ms ease`;
        el.style.opacity    = '1';
    }

    /**
     * Fade an element out (opacity 1→0), then hide it via hiddenClass or hidden attribute.
     */
    _fadeOutElement(el, hiddenClass) {
        if (el._fadeTimer) {
            clearTimeout(el._fadeTimer);
            el._fadeTimer = null;
        }

        el.style.transition = `opacity ${FADE_MS}ms ease`;
        el.style.opacity    = '0';

        el._fadeTimer = setTimeout(() => {
            if (hiddenClass) el.classList.add(hiddenClass);
            else el.hidden = true;
            el.style.transition = '';
            el.style.opacity    = '';
            el._fadeTimer = null;
        }, FADE_MS);
    }
}

customElements.define('brittfurn-mega-menu', BrittfurnMegaMenu);
